import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BrowserStorage } from '../../models/browserStorage';
import { Project } from '../../models/project';
import { DbService } from '../db/db.service';
import { environment } from '../../../environments/environment';
import { isValidString } from '@pineapplelab/util';
import { ConfigService } from '../config/config.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { ApiService } from '../api/api.service';
import { RoleEnum } from '../../enums/role.enum';
import { CategoryEnrollmentService } from '../category-enrollment/category-enrollment.service';

@Injectable({
	providedIn: 'root'
})
export class GetLanguageService {

	currentLanguage: string = BrowserStorage.get('language') || 'en';
	translationsLoaded: string = BrowserStorage.get('translationsLoaded');
	_projectLanguages: string[] = this.configService.getTenantLanguages();

	onLanguageChange = new Subject<{ language: string }>();

	private changingLanguage = new BehaviorSubject<boolean>(false);
	changingLanguage$ = this.changingLanguage.asObservable();

	private languageChangeGuard: ((language: string) => Promise<boolean>) | null = null;

	constructor(
		private translateService: TranslateService,
		private db: DbService,
		private configService: ConfigService,
		private authService: AuthService,
		private api: ApiService,
		private categoryEnrollmentService: CategoryEnrollmentService
	) { }

	async setLanguage(userLanguage: string, notify: boolean = true): Promise<void> {
		if (!userLanguage || !this._projectLanguages.includes(userLanguage)) {
			return;
		}
		const isLanguageChange = notify && this.currentLanguage !== userLanguage;
		if (isLanguageChange && this.languageChangeGuard) {
			const canChange = await this.languageChangeGuard(userLanguage);
			if (!canChange) {
				return;
			}
		}
		this.translateService.use(userLanguage);
		this.currentLanguage = userLanguage;
		BrowserStorage.set('appLanguage', userLanguage);
		if (isLanguageChange) {
			this.onLanguageChange.next({ language: userLanguage });
			this.changingLanguage.next(true);
			try {
				await this.notifyUserLanguageChange(userLanguage);
			} finally {
				this.changingLanguage.next(false);
			}
		}
	}

	registerLanguageChangeGuard(guard: (language: string) => Promise<boolean>): void {
		this.languageChangeGuard = guard;
	}

	clearLanguageChangeGuard(guard: (language: string) => Promise<boolean>): void {
		if (this.languageChangeGuard === guard) {
			this.languageChangeGuard = null;
		}
	}

	getDefaultLanguage(browserLanguage: string): string {
		const storedLanguage = BrowserStorage.get('appLanguage');
		if (storedLanguage) {
			return (this.currentLanguage = storedLanguage);
		}

		const defaultLanguage = this._projectLanguages.includes(browserLanguage) ? browserLanguage : 'en';
		return (this.currentLanguage = defaultLanguage);
	}

	getValue(key: string, lang?: string): string {
		const currentLang = lang || this.translateService.store?.currentLang || this.translateService.store?.defaultLang;
		const [firstPartKey, secondPartKey] = key.split('.');
		if (!this.translateService.store.translations?.[currentLang]) {
			return null;
		}
		return this.translateService.store.translations[currentLang]?.[firstPartKey]?.[secondPartKey];
	}

	async notifyUserLanguageChange(language: string): Promise<void> {
		const user = this.authService.auth.user;
		if (user?.role !== RoleEnum.CONTENT_CONSUMER && user?.role !== RoleEnum.CONTENT_CONSUMER_FAST_FORWARD) {
			return;
		}
		await Promise.allSettled([
			this.api.post('/user/change-language', { userId: user?.id, language }, { showAPIError: false }),
			this.categoryEnrollmentService.changeLanguage(user?.id, this.authService.projectId, language)
		]);
	}

}
